// src/pages/PreInscricoes/DetalhesPreInscricaoPage.js

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Typography, 
  Button, 
  Paper, 
  Grid, 
  Card, 
  CardContent, 
  Chip, 
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider // Importação adicionada
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack'; // Importação adicionada
import HistoryIcon from '@mui/icons-material/History';
import { obterPreInscricao, excluirPreInscricao } from '../../services/preInscricaoService';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import StatusHistorico from '../../components/PreInscricoes/StatusHistorico';

const DetalhesPreInscricaoPage = () => {
  const [preInscricao, setPreInscricao] = useState(null);
  const [historicoOpen, setHistoricoOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPreInscricao = async () => {
      try {
        const data = await obterPreInscricao(id);
        setPreInscricao(data);
      } catch (error) {
        console.error('Erro ao buscar detalhes da pré-inscrição:', error);
        setSnackbar({ 
          open: true, 
          message: 'Erro ao buscar inscrição', 
          severity: 'error' 
        });
      }
    };

    fetchPreInscricao();
  }, [id]);

  const handleExcluir = async () => {
    if (window.confirm('Tem certeza que deseja excluir esta pré-inscrição?')) {
      try {
        await excluirPreInscricao(id);
        navigate('/pre-inscricoes');
        setSnackbar({ 
          open: true, 
          message: 'Pré-inscrição excluída com sucesso', 
          severity: 'success' 
        });
      } catch (error) {
        console.error('Erro ao excluir pré-inscrição:', error);
        setSnackbar({ 
          open: true, 
          message: 'Erro ao excluir inscrição', 
          severity: 'error' 
        });
      }
    }
  };

  if (!preInscricao) {
    return <Typography>Carregando...</Typography>;
  }

  const isColegioInscricao = preInscricao.campanha.tipoInstituicao === 'Colégio';

  return (
    <Paper sx={{ p: 2 }}>
      {/* Botão Voltar */}
      <Box sx={{ mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/dashboard')}
          sx={{ mb: 2 }}
        >
          Voltar ao Dashboard
        </Button>
      </Box>

      <Card>
        <CardContent>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mb: 2 
          }}>
            <Typography variant="h4">
              Pré-Inscrição #{preInscricao.id}
            </Typography>
            <Chip 
              label={preInscricao.status.replace('_', ' ')} // Melhorar legibilidade
              color={
                preInscricao.status === 'Aprovada' ? 'success' :
                preInscricao.status === 'Pendente' ? 'warning' :
                preInscricao.status === 'Rejeitada' ? 'error' : 'default'
              }
              sx={{ mb: 2 }} 
            />
          </Box>

          <Divider sx={{ mb: 2 }} /> {/* Adicionada importação */}

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography>
                <strong>Estudante:</strong> {preInscricao.estudante.nome}
              </Typography>
              <Typography>
                <strong>Campanha:</strong> {preInscricao.campanha.nome}
              </Typography>
              <Typography>
                <strong>Instituição:</strong> {preInscricao.instituicao.nome}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography>
                <strong>Status:</strong> {preInscricao.status.replace('_', ' ')}
              </Typography>
              <Typography>
                <strong>Data de Submissão:</strong> {
                  new Date(preInscricao.createdAt).toLocaleString('pt-BR') // Especificar localidade
                }
              </Typography>
            </Grid>
            
            {isColegioInscricao ? (
              <Grid item xs={12}>
                <Typography>
                  <strong>Classe:</strong> {
                    preInscricao.classe?.numero || 'Não especificado'
                  }
                </Typography>
                <Typography>
                  <strong>Período:</strong> {
                    preInscricao.periodo?.nome || 'Não especificado'
                  }
                </Typography>
              </Grid>
            ) : (
              <Grid item xs={12}>
                <Typography>
                  <strong>Curso:</strong> {
                    preInscricao.curso?.nome || 'Não especificado'
                  }
                </Typography>
                <Typography>
                  <strong>Período:</strong> {
                    preInscricao.periodo?.nome || 'Não especificado'
                  }
                </Typography>
                <Typography>
                  <strong>Disciplina:</strong> {
                    preInscricao.disciplina?.nome || 'Não especificado'
                  }
                </Typography>
              </Grid>
            )}
          </Grid>

          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate(`/pre-inscricoes/editar/${id}`)}
            >
              Editar
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleExcluir}
            >
              Excluir
            </Button>
            <Button
              variant="outlined"
              startIcon={<HistoryIcon />}
              onClick={() => setHistoricoOpen(true)}
            >
              Ver Histórico
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Diálogo para mostrar o histórico */}
      <Dialog
        open={historicoOpen}
        onClose={() => setHistoricoOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Histórico de Status</DialogTitle>
        <DialogContent>
          <StatusHistorico preInscricaoId={id} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoricoOpen(false)} color="primary">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para feedback */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default DetalhesPreInscricaoPage;
